import * as Api from '@ViewModels';
import React from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { convertRawRichTextContentStateToRichContentEditorState } from '../../../../../models/UiUtils';
import { migrateLegacyDonationOptions } from '../../../../../surveys/models/DonationSurveyMigration';
import { DonationDonorInfoCollection, DonationServiceFeeCalculationMethod, DonationSurveyFormData } from '../utils';

/**
 * Props for the useDonationSurveyFormInitialization hook
 */
interface UseDonationSurveyFormInitializationProps {
	/** The donation survey to load into the form (undefined for new surveys) */
	survey?: Api.IDonationSurvey;
	/** React Hook Form function to set individual field values */
	setValue: UseFormSetValue<DonationSurveyFormData>;
}

/**
 * Helper function to convert donation options to form collection enum
 *
 * Maps the API's boolean flags (collect, require, updateExisting) to the
 * form's enum representation for donor information collection.
 *
 * @param options - Object containing collection flags from the API
 * @returns Enum value representing the collection mode
 */
const getDonationInfoCollectionFromOptions = (options?: {
	collect: boolean;
	require: boolean;
	updateExisting: boolean;
}): DonationDonorInfoCollection => {
	if (!options) {
		return DonationDonorInfoCollection.NO_COLLECT;
	}

	// Priority: require > collect/updateExisting > no_collect
	// When updateExisting is true, the public form shows the field (it's being collected),
	// so the admin UI should reflect that as "Collect" to prevent display mismatch.
	if (options.require) {
		return DonationDonorInfoCollection.REQUIRE;
	}
	if (options.collect || options.updateExisting) {
		return DonationDonorInfoCollection.COLLECT;
	}

	return DonationDonorInfoCollection.NO_COLLECT;
};

/**
 * Custom hook for initializing donation survey form data
 *
 * This hook populates the form with data from an existing donation survey
 * when editing. It only loads the data once to prevent overwriting user changes
 * during the editing session.
 *
 * Follows the pattern established in EventRegistrationSurvey and CustomSurvey
 * to maintain architectural consistency across all survey types.
 *
 * The hook handles initialization for:
 * - Basic survey information (name, title, description)
 * - Program-specific donation settings
 * - Expiration dates and images
 * - Donor information collection preferences
 * - Donation amount configuration (suggested amounts, custom amounts, minimums)
 * - Service fee settings (Stripe fees, custom fees)
 * - Custom form fields for additional questions
 *
 * @param props - Configuration including the survey data and form setter
 *
 * @example
 * useDonationSurveyFormInitialization({
 *   survey: existingSurvey,
 *   setValue: form.setValue
 * });
 */
export const useDonationSurveyFormInitialization = ({ survey, setValue }: UseDonationSurveyFormInitializationProps) => {
	// Track if we've loaded survey data to prevent reloading on every render
	// This ensures user edits aren't overwritten if the survey object reference changes
	const hasLoadedSurveyRef = React.useRef(false);

	React.useEffect(() => {
		if (!survey) {
			return;
		}

		// Only load survey data once when initially editing
		if (hasLoadedSurveyRef.current) {
			return;
		}

		hasLoadedSurveyRef.current = true;

		// ============================================================
		// Basic survey information
		// ============================================================
		setValue('name', survey.name);
		setValue('donationPageTitle', survey.donationOptions?.title || '');
		setValue('causeDetails', survey.donationOptions?.donationDescription || '');

		// ============================================================
		// Program-specific donations
		// Allow targeting donations to specific campaigns/programs
		// The toggle is ON when availableCampaignIds is defined and has items
		// If availableCampaignIds is undefined or empty array, toggle is OFF
		// ============================================================
		const hasAvailableCampaigns = Array.isArray(survey.availableCampaignIds) && survey.availableCampaignIds.length > 0;
		setValue('allowProgramSpecificDonations', hasAvailableCampaigns);
		// Only load campaigns if the toggle is ON
		setValue('programSpecificDonation', hasAvailableCampaigns ? survey.availableCampaigns || [] : []);
		// Campaign require setting (backward compat: default to false if missing)
		setValue('requireCampaignSelection', survey.donationOptions?.requireCampaignSelection ?? false);

		// ============================================================
		// Expiration settings
		// ============================================================
		setValue('hasExpirationDate', survey.expirationDate ? true : false);

		// ============================================================
		// Image settings
		// Header image for the donation page
		// ============================================================
		setValue('image', survey.donationOptions?.image || null);
		setValue('imageSource', survey.donationOptions?.image?.uploadSource || null);

		// ============================================================
		// Donor information collection
		// Control whether phone and address are collected/required
		// ============================================================
		const donorOptions = survey.donationOptions;
		const hasUpdateExisting =
			donorOptions?.collectDonorName?.updateExisting ||
			donorOptions?.collectDonorEmail?.updateExisting ||
			donorOptions?.collectDonorPhone?.updateExisting ||
			donorOptions?.collectDonorAddress?.updateExisting ||
			false;
		setValue('updateExistingContacts', hasUpdateExisting);
		setValue('collectDonorPhone', getDonationInfoCollectionFromOptions(survey.donationOptions?.collectDonorPhone));
		setValue('collectDonorAddress', getDonationInfoCollectionFromOptions(survey.donationOptions?.collectDonorAddress));

		// ============================================================
		// Donation receipt email
		// Hydrate the toggle so a disabled receipt survives reload; absent → keep the true default.
		// ============================================================
		if (survey.donationOptions?.sendConfirmationEmail !== undefined) {
			setValue('sendConfirmationEmail', survey.donationOptions.sendConfirmationEmail);
		}
		setValue(
			'confirmationMessage',
			convertRawRichTextContentStateToRichContentEditorState(survey.donationOptions?.confirmationMessage)
		);

		// ============================================================
		// Donation amount settings - with migration support
		// Migrate legacy flat structure to new frequency-specific structure
		// ============================================================
		const migratedOptions = migrateLegacyDonationOptions(
			survey.donationOptions,
			survey.donationOptions?.allowRecurringDonations || false
		);

		// Legacy fields (kept for backward compatibility)
		setValue('hasDonationValues', survey.donationOptions?.enableSuggestedAmounts || false);
		setValue('donationValues', survey.donationOptions?.suggestedAmounts?.map(amount => ({ value: amount })) || []);
		setValue('allowCustomDonationAmount', survey.donationOptions.allowCustomAmount || false);
		setValue('hasMinimumDonationAmount', survey.donationOptions.requireMinimumAmount || false);
		setValue('minimumDonationAmount', survey.donationOptions.minimumAmount || 0);

		// New frequency-specific settings
		if (migratedOptions.oneTimeDonationSettings) {
			setValue('oneTimeSettings', {
				enabled: migratedOptions.oneTimeDonationSettings.enabled,
				hasDonationValues: migratedOptions.oneTimeDonationSettings.enableSuggestedAmounts,
				donationValues: migratedOptions.oneTimeDonationSettings.suggestedAmounts.map(amount => ({ value: amount })),
				allowCustomDonationAmount: migratedOptions.oneTimeDonationSettings.allowCustomAmount,
				hasMinimumDonationAmount: migratedOptions.oneTimeDonationSettings.requireMinimumAmount,
				minimumDonationAmount: migratedOptions.oneTimeDonationSettings.minimumAmount,
			});
		}

		if (migratedOptions.monthlyDonationSettings) {
			setValue('monthlySettings', {
				enabled: migratedOptions.monthlyDonationSettings.enabled,
				hasDonationValues: migratedOptions.monthlyDonationSettings.enableSuggestedAmounts,
				donationValues: migratedOptions.monthlyDonationSettings.suggestedAmounts.map(amount => ({ value: amount })),
				allowCustomDonationAmount: migratedOptions.monthlyDonationSettings.allowCustomAmount,
				hasMinimumDonationAmount: migratedOptions.monthlyDonationSettings.requireMinimumAmount,
				minimumDonationAmount: migratedOptions.monthlyDonationSettings.minimumAmount,
			});
		}

		if (migratedOptions.annualDonationSettings) {
			setValue('annualSettings', {
				enabled: migratedOptions.annualDonationSettings.enabled,
				hasDonationValues: migratedOptions.annualDonationSettings.enableSuggestedAmounts,
				donationValues: migratedOptions.annualDonationSettings.suggestedAmounts.map(amount => ({ value: amount })),
				allowCustomDonationAmount: migratedOptions.annualDonationSettings.allowCustomAmount,
				hasMinimumDonationAmount: migratedOptions.annualDonationSettings.requireMinimumAmount,
				minimumDonationAmount: migratedOptions.annualDonationSettings.minimumAmount,
			});
		}

		// ============================================================
		// Service fee settings
		// Configure how payment processing fees are handled
		// Options: Stripe's fees, custom fixed amount, or custom percentage
		// ============================================================
		setValue('allowCustomerToCoverServiceFees', survey.serviceFeeSettings?.allowCustomerToCoverServiceFees || false);
		setValue(
			'serviceFeeCalculationMethod',
			(survey.serviceFeeSettings?.calculationMethod as DonationServiceFeeCalculationMethod) ||
				DonationServiceFeeCalculationMethod.STRIPE
		);
		setValue('serviceFeeFixedAmount', survey.serviceFeeSettings?.fixedFeeAmount || 0);
		setValue('serviceFeePercentage', survey.serviceFeeSettings?.percentageFee || 0);

		// ============================================================
		// Custom form fields
		// Additional questions to ask donors during the donation flow
		// ============================================================
		setValue('customForm', survey.customForm);
		setValue('tagGenerationSettings', survey.tagGenerationSettings);

		// ============================================================
		// SurveyForm — conditional visibility rules ("Only show when")
		// AdditionalQuestions hydrates the in-memory map from this on first read.
		// ============================================================
		if (survey.surveyForm) {
			setValue('surveyForm', survey.surveyForm);
		}

		// ============================================================
		// Notification settings
		// Email notification preferences for survey responses
		// ============================================================
		if (survey.notificationSettings) {
			setValue('notificationSettings', {
				_type: 'SurveyNotificationSettings',
				isEnabled: survey.notificationSettings.isEnabled || false,
				additionalRecipients: survey.notificationSettings.additionalRecipients || [],
			});
		}
	}, [survey, setValue]);
};
